<script lang="ts">
  // UEBERGANG (docs/shell.md, Abschnitt "Mobil").
  //
  // Auf dem Telefon gibt es die Shell NICHT: dort steht ein eigenes
  // Bedienkonzept an (Status sehen, Licht schalten -- kein Werkzeug).
  // Bis das gebaut ist, laeuft dort weiter die heutige Navigation.
  //
  // Diese Datei fuellt genau die eine Luecke, die dadurch entsteht:
  // Seiten wie Docker oder Entgelt oeffnen Tabs, die keine Seite sind
  // (ein Container, ein Beleg). Ohne Arbeitsflaeche waere so ein Tab
  // auf dem Telefon unsichtbar -- der Klick liefe ins Leere. Hier
  // bekommt er eine Vollbild-Flaeche, wie vor dem Umbau.
  //
  // Wenn das Mobil-Konzept steht, faellt diese Datei ersatzlos weg.
  //
  // Der Rueckweg (Tim 07.10.2026: "Wenn man ein Dokument gross anzeigt,
  // dann kommt man nicht mehr zurueck"). Bis dahin sass der einzige Knopf
  // ("Schliessen") oben rechts in einer Kopfzeile ohne Abstand zur
  // Statusleiste. In der installierten App reicht die Seite bis unter Uhr
  // und Akku (viewport-fit=cover) -- dort kommt kein Tippen an. Jetzt:
  //   - die Kopfzeile beginnt UNTER der Statusleiste (safe-area),
  //   - links steht "Zurueck", wo man es auf dem Telefon sucht, in
  //     Daumen-Groesse; rechts bleibt das Kreuz,
  //   - die Zurueck-Geste des Telefons (Wischen vom Rand, Zurueck-Taste)
  //     schliesst das Blatt, statt die Seite darunter zu verlassen,
  //   - Esc schliesst auch.

  import Icon from "../Icon.svelte";
  import { t } from "../i18n.svelte";
  import { KREUZ_PX } from "../kreuz";
  import { portal } from "../portal";
  import { getPanel, titelVon, untertitelVon } from "./registry";
  import { aktiverTab, schliesseTab, shellFokus } from "./tabs.svelte";
  import { ladeBaustein } from "../baustein";

  const tab = $derived(aktiverTab());
  const zeigen = $derived(!!tab && tab.typ !== "page");

  // Eigener Eintrag im Verlauf, solange das Blatt offen ist: dann ist
  // "zurueck" am Telefon das Schliessen des Blatts. Dieselbe Adresse --
  // der Router (hashchange) merkt davon nichts.
  let eintrag = false;
  $effect(() => {
    if (!zeigen) {
      eintrag = false;
      return;
    }
    window.history.pushState({ blatt: true }, "");
    eintrag = true;
    const zurueck = () => {
      // Der Eintrag ist verbraucht; jetzt nur noch den Tab schliessen.
      eintrag = false;
      const offen = aktiverTab();
      if (offen && offen.typ !== "page") schliesseTab(shellFokus(), offen.id);
    };
    window.addEventListener("popstate", zurueck);
    return () => window.removeEventListener("popstate", zurueck);
  });

  function schliessen(): void {
    // Ueber den Verlauf, damit der eigene Eintrag nicht liegen bleibt
    // (sonst braucht es danach einen Zurueck-Schritt ins Leere).
    if (eintrag) {
      window.history.back();
      return;
    }
    const offen = aktiverTab();
    if (offen && offen.typ !== "page") schliesseTab(shellFokus(), offen.id);
  }
</script>

<svelte:window onkeydown={(e) => zeigen && e.key === "Escape" && schliessen()} />

{#if zeigen && tab}
  {@const def = getPanel(tab.typ)}
  {@const ident = tab.params as Record<string, unknown>}
  <div use:portal class="blatt bg-bg fixed inset-0 z-190 flex flex-col">
    <header class="kopf border-border bg-bg-muted flex items-center gap-1 border-b">
      <button type="button" class="knopf" onclick={schliessen} aria-label={t("common.back", "Zurück")}>
        <Icon name="chevron-left" size={22} />
      </button>
      <div class="min-w-0 flex-1">
        <p class="text-fg t-body truncate font-semibold">{titelVon(tab.typ, ident)}</p>
        {#if untertitelVon(tab.typ, ident)}
          <p class="text-fg-subtle t-meta truncate">{untertitelVon(tab.typ, ident)}</p>
        {/if}
      </div>
      <button type="button" class="knopf" onclick={schliessen} aria-label={t("shell.close", "Schließen")}>
        <Icon name="x" size={KREUZ_PX} />
      </button>
    </header>
    <div class="inhalt min-h-0 flex-1 overflow-auto">
      {#if def}
        {#await ladeBaustein(`panel ${tab.typ}`, def.load) then mod}
          {@const Panel = mod.default}
          <Panel {...tab.params} onclose={schliessen} />
        {/await}
      {/if}
    </div>
  </div>
{/if}

<style>
  /* Die Seite reicht bis unter die Statusleiste und neben die Kerbe
     (viewport-fit=cover): Kopfzeile und Inhalt halten Abstand dazu. */
  .kopf {
    padding: max(0.35rem, var(--sicher-oben)) max(0.25rem, env(safe-area-inset-right)) 0.35rem
      max(0.25rem, env(safe-area-inset-left));
  }
  .inhalt {
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }
  /* Daumen-Groesse: 44 px sind das Mass, unter dem man auf dem Telefon
     danebentippt. */
  .knopf {
    display: grid;
    flex: none;
    place-items: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.6rem;
    color: var(--fg-muted);
  }
  .knopf:active {
    background: color-mix(in oklab, var(--fg) 10%, transparent);
    color: var(--fg);
  }
</style>
